import { criterion, fromCriteria } from "@/core/check";
import type { Lesson } from "@/core/types";
import { pruefeMitMakro } from "@/office/makro/pruefung";
import { MAKRO_UMBRUCH } from "../makros";

export const lektionUmbruchsteuerung: Lesson = {
  id: "umbruchsteuerung",
  title: "Umbruchsteuerung: was zusammengehört, bleibt zusammen",
  summary: "Überschriften am Seitenende, einsame Zeilen, Kapitel auf neuer Seite – alles eine Einstellung.",
  durationMinutes: 9,
  learningGoals: [
    "Absatzkontrolle, „Nicht vom nächsten trennen“ und „Diesen Absatz zusammenhalten“ unterscheiden",
    "Einen Absatz an den folgenden binden",
    "Kapitel über „Seitenumbruch oberhalb“ in der Formatvorlage auf eine neue Seite bringen",
  ],
  steps: [
    {
      id: "vier-optionen",
      title: "Vier Optionen im Absatz-Dialog",
      explanation: [
        "Im Absatz-Dialog (Start → kleiner Pfeil in der Gruppe „Absatz“) liegt die " +
          "Registerkarte „Zeilen- und Seitenumbruch“. Dort stehen vier Optionen, die " +
          "bestimmen, wie Word einen Absatz auf Seiten verteilt.",
        "„Absatzkontrolle“ verhindert, dass die erste oder letzte Zeile eines Absatzes " +
          "allein auf einer Seite steht – Schusterjungen und Hurenkinder in der Sprache der " +
          "Setzer. Sie ist standardmäßig an und sollte es bleiben.",
        "„Nicht vom nächsten Absatz trennen“ bindet einen Absatz an den folgenden. Alle " +
          "Überschriftenvorlagen haben das bereits – deshalb landet eine Überschrift nie " +
          "allein am Seitenende. „Diesen Absatz zusammenhalten“ verhindert, dass ein " +
          "Absatz überhaupt auf zwei Seiten verteilt wird.",
        "„Seitenumbruch oberhalb“ schließlich beginnt vor dem Absatz eine neue Seite.",
      ],
      keyPoints: [
        "Absatz-Dialog → Zeilen- und Seitenumbruch.",
        "Überschriften sind schon an den folgenden Absatz gebunden.",
        "Die Absatzkontrolle bleibt eingeschaltet.",
      ],
    },
    {
      id: "zusammenhalten",
      title: "Einen Absatz an den nächsten binden",
      explanation: [
        "Ein typischer Fall: Ein Satz kündigt eine Tabelle oder Aufzählung an – „Die " +
          "Ergebnisse im Überblick:“ – und landet am Seitenende, während die Tabelle auf der " +
          "nächsten Seite beginnt. Die übliche Behelfslösung sind Leerzeilen, bis der Satz " +
          "rüberrutscht. Sie stehen nach der nächsten Änderung an der falschen Stelle.",
        "Richtig ist „Nicht vom nächsten Absatz trennen“ am ankündigenden Satz. Word hält " +
          "dann beide zusammen, egal wie sich der Text davor verändert.",
      ],
      keyPoints: [
        "Ankündigungssätze an die folgende Tabelle oder Liste binden.",
        "Leerzeilen sind keine Umbruchsteuerung.",
      ],
      task: {
        instruction:
          "Setze bei einem Fließtext-Absatz, der eine Liste oder Tabelle ankündigt, die " +
          "Option „Nicht vom nächsten Absatz trennen“.",
        hint: "Cursor in den Absatz, Absatz-Dialog → Zeilen- und Seitenumbruch → Häkchen setzen.",
        macro: MAKRO_UMBRUCH,
        check: () =>
          pruefeMitMakro(MAKRO_UMBRUCH, (bericht) => {
            const zusammen = bericht.zahl("fliesstextZusammengehalten") ?? 0;
            return fromCriteria(
              [criterion(`Ein Fließtext-Absatz ist an den nächsten gebunden (gefunden: ${zusammen})`, zusammen > 0)],
              {
                success: "Ankündigung und Inhalt bleiben jetzt auf derselben Seite.",
                failure: "Noch kein Fließtext-Absatz ist an seinen Nachfolger gebunden.",
                hint: "Die Option steht auf der zweiten Registerkarte des Absatz-Dialogs.",
              },
            );
          }),
      },
    },
    {
      id: "seitenumbruch-oberhalb",
      title: "Jedes Kapitel auf einer neuen Seite",
      explanation: [
        "Sollen Hauptkapitel immer oben auf einer Seite beginnen, greifen viele zum " +
          "manuellen Seitenumbruch (Strg + Enter) vor jeder Überschrift. Das ist dieselbe " +
          "Behelfslösung wie die Leerzeile: Wird ein Kapitel kürzer oder verschoben, " +
          "entsteht eine leere Seite.",
        "Die saubere Lösung ist eine Eigenschaft der Formatvorlage: „Überschrift 1“ " +
          "ändern → „Format“ → „Absatz“ → „Zeilen- und Seitenumbruch“ → „Seitenumbruch " +
          "oberhalb“. Ab dann beginnt jedes Hauptkapitel auf einer neuen Seite – auch jedes, " +
          "das erst später dazukommt.",
      ],
      keyPoints: [
        "Seitenumbruch oberhalb in der Vorlage „Überschrift 1“.",
        "Keine manuellen Seitenumbrüche vor Überschriften.",
      ],
      task: {
        instruction:
          "Lege mindestens zwei Überschriften der Ebene 1 an und stelle in der Formatvorlage " +
          "„Überschrift 1“ „Seitenumbruch oberhalb“ ein. Vor den Überschriften soll kein " +
          "manueller Seitenumbruch stehen.",
        hint: "Rechtsklick auf „Überschrift 1“ im Katalog → Ändern → Format → Absatz.",
        macro: MAKRO_UMBRUCH,
        check: () =>
          pruefeMitMakro(MAKRO_UMBRUCH, (bericht) => {
            const ueberschriften = bericht.zahl("ueberschriften") ?? 0;
            const inVorlage = bericht.janein("vorlage.umbruchOberhalb") === true;
            const vonHand = bericht.zahl("umbruecheVonHand") ?? 0;
            return fromCriteria(
              [
                criterion(`Mindestens zwei Überschriften der Ebene 1 (gefunden: ${ueberschriften})`, ueberschriften >= 2),
                criterion("„Seitenumbruch oberhalb“ steckt in der Vorlage „Überschrift 1“", inVorlage),
                criterion(`Keine manuellen Seitenumbrüche vor Überschriften (gefunden: ${vonHand})`, vonHand === 0),
              ],
              {
                success: "Jedes Hauptkapitel beginnt jetzt von selbst auf einer neuen Seite.",
                failure: "Die Kapitel beginnen noch nicht über die Vorlage auf einer neuen Seite.",
                hint:
                  "Manuelle Umbrüche findest du mit eingeblendeten Absatzmarken (¶) als " +
                  "„Seitenumbruch“-Zeile. Löschen, wenn die Vorlage die Aufgabe übernimmt.",
              },
            );
          }),
      },
    },
  ],
};
